import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const emptyForm = {
  itemCode: "",
  itemName: "",
  hsnCode: "",
  categoryId: "",
  groupId: "",
  unitId: "",
  unit: "Nos",
  gstRateId: "",
  costPrice: 0,
  salePrice: 0,
  gstPercent: 18,
  openingStock: 0,
};
const val = (r, ...keys) =>
  keys.map((k) => r?.[k]).find((x) => x !== undefined && x !== null) ?? "";
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const styles = {
  page: { fontSize: 11, minHeight: "100%", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  tableWrap: {
    maxHeight: 420,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 1350, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function ItemsPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [masters, setMasters] = useState({
    categories: [],
    groups: [],
    units: [],
    gstRates: [],
  });
  const [quick, setQuick] = useState({
    categoryName: "",
    groupName: "",
    unitName: "",
    unitSymbol: "",
  });

  const loadMasters = async () => {
    const cid = companyId();
    const [c, g, u, gst] = await Promise.all([
      client.get(`/inventory/item-categories?companyId=${cid}`),
      client.get(`/inventory/item-groups?companyId=${cid}`),
      client.get(`/inventory/units?companyId=${cid}`),
      client.get("/inventory/gst-rates"),
    ]);
    setMasters({
      categories: c.data || [],
      groups: g.data || [],
      units: u.data || [],
      gstRates: gst.data || [],
    });
  };

  const load = async () => {
    const res = await client.get(`/inventory/items?companyId=${companyId()}`);
    setRows(
      Array.isArray(res.data)
        ? res.data.filter((x) => x.IsActive ?? x.isActive ?? true)
        : [],
    );
  };

  useEffect(() => {
    loadMasters();
    load();
  }, []);

  const gstOptions = useMemo(
    () =>
      masters.gstRates.map((r) => ({
        id: val(r, "Id", "id"),
        name: val(r, "RateName", "rateName"),
        percent: Number(val(r, "GSTPercent", "gstPercent") || 0),
      })),
    [masters.gstRates],
  );

  const onGstChange = (id) => {
    const rate = gstOptions.find((x) => String(x.id) === String(id));
    setForm({ ...form, gstRateId: id, gstPercent: rate ? rate.percent : 0 });
  };

  const onUnitChange = (id) => {
    const unit = masters.units.find(
      (x) => String(val(x, "Id", "id")) === String(id),
    );
    setForm({
      ...form,
      unitId: id,
      unit:
        val(unit, "UnitSymbol", "unitSymbol") ||
        val(unit, "UnitName", "unitName") ||
        "Nos",
    });
  };

  const saveMaster = async (type) => {
    const cid = companyId();
    if (type === "category") {
      if (!quick.categoryName.trim()) return alert("Enter category name");
      await client.post("/inventory/item-categories", {
        companyId: cid,
        categoryName: quick.categoryName.trim(),
      });
      setQuick({ ...quick, categoryName: "" });
    }
    if (type === "group") {
      if (!quick.groupName.trim()) return alert("Enter group name");
      await client.post("/inventory/item-groups", {
        companyId: cid,
        groupName: quick.groupName.trim(),
      });
      setQuick({ ...quick, groupName: "" });
    }
    if (type === "unit") {
      if (!quick.unitName.trim()) return alert("Enter unit name");
      await client.post("/inventory/units", {
        companyId: cid,
        unitName: quick.unitName.trim(),
        unitSymbol: quick.unitSymbol.trim() || quick.unitName.trim(),
      });
      setQuick({ ...quick, unitName: "", unitSymbol: "" });
    }
    await loadMasters();
  };

  const save = async () => {
    if (!form.itemCode || !form.itemName || !form.hsnCode)
      return alert("Please fill Item Code, Item Name and HSN Code");
    if (!form.categoryId) return alert("Please select Item Category");
    if (!form.groupId) return alert("Please select Item Group");
    if (!form.unitId) return alert("Please select Unit of Measure");
    if (!form.gstRateId) return alert("Please select GST Rate");
    const payload = {
      ...form,
      companyId: companyId(),
      categoryId: Number(form.categoryId),
      groupId: Number(form.groupId),
      unitId: Number(form.unitId),
      gstRateId: Number(form.gstRateId),
      costPrice: Number(form.costPrice || 0),
      salePrice: Number(form.salePrice || 0),
      gstPercent: Number(form.gstPercent || 0),
      openingStock: Number(form.openingStock || 0),
    };
    try {
      setSaving(true);
      if (editingId) await client.put(`/inventory/items/${editingId}`, payload);
      else await client.post("/inventory/items", payload);
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e) {
      alert(
        e.response?.data?.message || e.response?.data || "Unable to save item",
      );
    } finally {
      setSaving(false);
    }
  };

  const editRow = (r) => {
    setEditingId(val(r, "Id", "id"));
    setForm({
      itemCode: val(r, "ItemCode", "itemCode"),
      itemName: val(r, "ItemName", "itemName"),
      hsnCode: val(r, "HSNCode", "hsnCode"),
      categoryId: val(r, "CategoryId", "categoryId") || "",
      groupId: val(r, "GroupId", "groupId") || "",
      unitId: val(r, "UnitId", "unitId") || "",
      unit: val(r, "Unit", "unit") || "Nos",
      gstRateId: val(r, "GSTRateId", "gstRateId") || "",
      costPrice: val(r, "CostPrice", "costPrice") || 0,
      salePrice: val(r, "SalePrice", "salePrice") || 0,
      gstPercent: val(r, "GSTPercent", "gstPercent", "TaxPercent") || 18,
      openingStock: val(r, "OpeningStock", "openingStock", "OpeningQty") || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };
  const deleteRow = async (r) => {
    if (!window.confirm("Deactivate this item?")) return;
    await client.delete(`/inventory/items/${val(r, "Id", "id")}`);
    await load();
  };

  const input = (key, label, type = "text") => (
    <div className="col-md-2">
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        className="form-control form-control-sm"
        style={styles.input}
        value={form[key]}
        placeholder={label}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );
  const select = (key, label, list, textKeys, onChange) => (
    <div className="col-md-2">
      <label style={styles.label}>{label}</label>
      <select
        className="form-select form-select-sm"
        style={styles.input}
        value={form[key]}
        onChange={(e) =>
          onChange
            ? onChange(e.target.value)
            : setForm({ ...form, [key]: e.target.value })
        }
      >
        <option value="">Select {label}</option>
        {list.map((x) => (
          <option key={val(x, "Id", "id")} value={val(x, "Id", "id")}>
            {textKeys
              .map((k) => val(x, k, k.charAt(0).toLowerCase() + k.slice(1)))
              .filter(Boolean)
              .join(" - ")}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-box-seam me-2"></i>Items
          </h6>
          <div style={styles.sub}>
            Item category, item group, unit of measure and GST rate dropdowns
            are enabled
          </div>
        </div>
        <div style={styles.body}>
          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8f9fa",
              borderRadius: 10,
              border: "1px solid #e9ecef",
            }}
          >
            <div className="col-md-3">
              <label style={styles.label}>New Item Category</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.categoryName}
                  onChange={(e) =>
                    setQuick({ ...quick, categoryName: e.target.value })
                  }
                  placeholder="Example: Finished Goods"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("category")}
                >
                  Add
                </button>
              </div>
            </div>
            <div className="col-md-3">
              <label style={styles.label}>New Item Group</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.groupName}
                  onChange={(e) =>
                    setQuick({ ...quick, groupName: e.target.value })
                  }
                  placeholder="Example: Electronics"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("group")}
                >
                  Add
                </button>
              </div>
            </div>
            <div className="col-md-4">
              <label style={styles.label}>New Unit of Measure</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.unitName}
                  onChange={(e) =>
                    setQuick({ ...quick, unitName: e.target.value })
                  }
                  placeholder="Unit Name"
                />
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.unitSymbol}
                  onChange={(e) =>
                    setQuick({ ...quick, unitSymbol: e.target.value })
                  }
                  placeholder="Symbol"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("unit")}
                >
                  Add
                </button>
              </div>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                style={styles.btn}
                onClick={loadMasters}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>Refresh Masters
              </button>
            </div>
          </div>
          {editingId && (
            <div
              className="alert alert-warning py-1 mb-2"
              style={{ fontSize: 11 }}
            >
              Editing item #{editingId}. Save to update or cancel to create a
              new item.
            </div>
          )}
          <div className="row g-2 mb-2">
            {input("itemCode", "Item Code")}
            {input("itemName", "Item Name")}
            {input("hsnCode", "HSN Code")}
            {select("categoryId", "Item Category", masters.categories, [
              "CategoryName",
            ])}
            {select("groupId", "Item Group", masters.groups, ["GroupName"])}
            {select(
              "unitId",
              "Unit of Measure",
              masters.units,
              ["UnitSymbol", "UnitName"],
              onUnitChange,
            )}
            <div className="col-md-2">
              <label style={styles.label}>GST Rate</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.gstRateId}
                onChange={(e) => onGstChange(e.target.value)}
              >
                <option value="">Select GST Rate</option>
                {gstOptions.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} ({x.percent}%)
                  </option>
                ))}
              </select>
            </div>
            {input("costPrice", "Cost Price", "number")}
            {input("salePrice", "Sale Price", "number")}
            {input("openingStock", "Opening Stock", "number")}
            <div className="col-md-1">
              <label style={styles.label}>GST %</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.gstPercent}
                readOnly
              />
            </div>
            <div className="col-md-2 d-flex gap-1 align-items-end">
              <button
                className={`btn btn-${editingId ? "primary" : "success"} btn-sm flex-fill`}
                style={styles.btn}
                onClick={save}
                disabled={saving}
              >
                <i className="bi bi-check2-circle me-1"></i>
                {saving ? "Saving..." : editingId ? "Update" : "Save"}
              </button>
              {editingId && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  style={styles.btn}
                  onClick={cancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Total Active Items: <strong>{rows.length}</strong>
            </span>
            <span>
              GST Rate includes Zero Rated, Exempted, Non GST, Composition, Nil
              Rated, 5, 12, 18 and 28
            </span>
          </div>
          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Name",
                    "Category",
                    "Group",
                    "HSN Code",
                    "Unit",
                    "GST Rate",
                    "GST %",
                    "Cost",
                    "Sale",
                    "Opening Stock",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Cost", "Sale", "GST %", "Opening Stock"].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="12"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No items found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={val(r, "Id", "id") || i}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {val(r, "ItemCode", "itemCode")}
                      </td>
                      <td style={styles.td}>
                        {val(r, "ItemName", "itemName")}
                      </td>
                      <td style={styles.td}>
                        {val(r, "CategoryName", "categoryName")}
                      </td>
                      <td style={styles.td}>
                        {val(r, "GroupName", "groupName")}
                      </td>
                      <td style={styles.td}>{val(r, "HSNCode", "hsnCode")}</td>
                      <td style={styles.td}>
                        {val(r, "UnitSymbol", "unitSymbol") ||
                          val(r, "Unit", "unit")}
                      </td>
                      <td style={styles.td}>
                        {val(r, "GSTRateName", "gstRateName")}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(
                          val(r, "GSTPercent", "gstPercent", "TaxPercent") || 0,
                        ).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(val(r, "CostPrice", "costPrice") || 0).toFixed(
                          2,
                        )}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(val(r, "SalePrice", "salePrice") || 0).toFixed(
                          2,
                        )}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(
                          val(
                            r,
                            "OpeningStock",
                            "openingStock",
                            "OpeningQty",
                          ) || 0,
                        ).toFixed(2)}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          style={{ fontSize: 10 }}
                          onClick={() => editRow(r)}
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: 10 }}
                          onClick={() => deleteRow(r)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
